import styled from 'styled-components';
import { useEffect, useState } from 'react';
import { priceFormat, roundPrice } from '../priceFormat';

interface Props {
  vehicle:{
    completeName:string,
    clientPrice?: number;
    avgPrice?: number;
    minPrice?: number;
    maxPrice?: number;
    public: boolean;
  }
}

export default function PurchasePriceRange(props:Readonly<Props>) {
  const { vehicle } = props;
  const isPublic = vehicle.public;

  const [minValue, setMinValue] = useState(0);
  const [maxValue, setMaxValue] = useState(0);

  useEffect(() => {
    const allValues = [
      vehicle.clientPrice && !vehicle.public ? vehicle.clientPrice : 0,
      vehicle.avgPrice ?? 0,
      vehicle.minPrice ?? 0,
      vehicle.maxPrice ?? 0,
    ].filter((value) => typeof value === 'number' && !Number.isNaN(value) && value > 0); // && value > 0

    if (allValues.length > 0) {
      const min = Math.min(...allValues);
      const max = Math.max(...allValues);

      setMinValue(min);
      setMaxValue(max);
    } else {
      setMinValue(0);
      setMaxValue(0);
    }

    if (vehicle.clientPrice === 0 && vehicle.minPrice && vehicle.clientPrice < vehicle.minPrice) {
      setMinValue(vehicle.clientPrice);
    }
  }, [vehicle]);

  const range = maxValue - minValue;

  const difClientPrice = vehicle.clientPrice ? vehicle.clientPrice - minValue : 0;
  const difAvgPrice = vehicle.avgPrice ? vehicle.avgPrice - minValue : 0;
  const difMinPrice = vehicle.minPrice ? vehicle.minPrice - minValue : 0;
  const difMaxPrice = vehicle.maxPrice ? vehicle.maxPrice - minValue : 0;

  let percentageClientPrice = Math.round((difClientPrice * 100) / range);
  if (range === 0) {
    percentageClientPrice = 50;
  }
  const percentageAvgPrice = Math.round((difAvgPrice * 100) / range);
  const percentageMinPrice = Math.round((difMinPrice * 100) / range);
  const percentageMaxPrice = Math.round((difMaxPrice * 100) / range);

  const renderBoxes = () => {
    const boxesTop: React.ReactNode[] = [];
    const boxesBottom: React.ReactNode[] = [];

    const customDataMap = new Map();

    if (!isPublic) {
      customDataMap.set(`user-${percentageClientPrice}`, {
        isUserPrice: true,
        title: 'Precio de retoma usuario',
        value: `$${priceFormat(roundPrice(vehicle.clientPrice ? vehicle.clientPrice : 0, 1))}`,
      });
    }

    if (vehicle.avgPrice) {
      customDataMap.set(`purchase-${percentageAvgPrice}`, {
        isUserPrice: false,
        title: 'Precio promedio parte de pago',
        value: `$${priceFormat(roundPrice(vehicle.avgPrice ? vehicle.avgPrice : 0, 5))}`,
      });
    }

    if (vehicle.minPrice) {
      customDataMap.set(`purchase-${percentageMinPrice}`, {
        isUserPrice: false,
        title: 'Precio mínimo parte de pago',
        value: `$${priceFormat(roundPrice(vehicle.minPrice ? vehicle.minPrice : 0, 5))}`,
      });
    }

    if (vehicle.maxPrice) {
      customDataMap.set(`purchase-${percentageMaxPrice}`, {
        isUserPrice: false,
        title: 'Precio máximo parte de pago',
        value: `$${priceFormat(roundPrice(vehicle.maxPrice ? vehicle.maxPrice : 0, 5))}`,
      });
    }

    // eslint-disable-next-line no-plusplus
    for (let i = 0; i <= 100; i++) {
      const customDataUser = customDataMap.get(`user-${i}`);
      const customDataPurchase = customDataMap.get(`purchase-${i}`);

      // boxes top
      if (customDataUser) {
        boxesTop.push(
          <TopPrice
            key={i}
            title={`${customDataUser.title}`}
            value={customDataUser.value}
            showData={customDataUser.isUserPrice}
          />,
        );
      } else if (customDataPurchase) {
        boxesTop.push(
          <BottomPrice key={i} title={customDataPurchase.title} value={customDataPurchase.value} showData={customDataPurchase.isUserPrice} />,
        );
      } else {
        boxesTop.push(<EmtyBoxContainer key={i} />);
      }

      // boxes bottom
      if (customDataPurchase) {
        boxesBottom.push(
          <BottomPrice
            key={i}
            title={`${customDataPurchase.title}`}
            value={customDataPurchase.value}
            showData={!customDataPurchase.isUserPrice}
          />,
        );
      } else if (customDataUser) {
        boxesBottom.push(<TopPrice key={i} title={customDataUser.title} value={customDataUser.value} showData={!customDataUser.isUserPrice} />);
      } else {
        boxesBottom.push(<EmtyBoxContainer key={i} />);
      }
    }

    return [boxesTop, boxesBottom];
  };

  const isMobile = window.innerWidth <= 700;
  const displayBoxes = renderBoxes();

  return (
    <Container>
      { !isPublic && (
        <SubCont3>
          {isMobile ? displayBoxes[0].slice().reverse() : displayBoxes[0]}
        </SubCont3>
      )}
      <SubCont1>
        <BarContainer />
      </SubCont1>
      <SubCont3>
        {isMobile ? displayBoxes[1].slice().reverse() : displayBoxes[1]}
      </SubCont3>
    </Container>
  );
}

function TopPrice({ title, value, showData }: Readonly<{ title: string, value: string | null, showData: boolean }>) {
  return (
    <AutoIconTextContainer>
      <TitleValueContainer>
        <TitleTopPrice showData={showData}>
          {title}
        </TitleTopPrice>
        <BlankSpace width="0px" height="0px" />
        <PriceText showData={showData}>
          {value}
        </PriceText>
        <BlankSpace width="0px" height="5px" />
      </TitleValueContainer>
      { showData ? <IndicatorTriangleTop /> : '' }
    </AutoIconTextContainer>
  );
}

function BottomPrice({
  title, value, showData,
}: Readonly<{ title: string, value: string | null, showData?: boolean }>) {
  return (
    <AutoIconTextContainer2>
      { showData ? <IndicatorTriangleBottom /> : '' }
      <TitleValueContainer2>
        <BlankSpace width="0px" height="5px" />
        <PriceText showData={showData}>
          {value}
        </PriceText>
        <TitlePrice showData={showData}>
          {title}
        </TitlePrice>
      </TitleValueContainer2>
    </AutoIconTextContainer2>
  );
}

const Container = styled.div`
  padding: 20px 0 20px 0;
  display: flex;
  flex-wrap: nowrap;
  flex-direction: column;
  @media (max-width: 700px) {
    flex-direction: column;
    align-items: center;
  }
`;

const EmtyBoxContainer = styled.div`
  width: 0px;
  margin: 0px 0px;
  display: flex;
  flex-direction: column;
  align-items: center;
  @media (max-width: 700px) {
    flex-direction: row;
    align-items: flex-end;
    justify-content: flex-end;
    width: auto;
    margin: 1px 0;
  }
`;

const SubCont3 = styled.div`
  width: 90%;
  min-width: 250px;
  display: flex;
  flex-direction: row;
  margin: auto;
  justify-content: space-between;
  @media (max-width: 700px) {
    flex-direction: column;
    width: auto;
    min-width: unset;
    height: 100%;
    margin: 0px;
    justify-content: flex-end;
    align-items: flex-end;
    flex: 1;
  }
`;

const SubCont1 = styled.div`
  width: 90%;
  min-width: 250px;
  display: flex;
  flex-direction: row;
  margin: auto;
  justify-content: center;
  @media (max-width: 700px) {
    flex-direction: column;
    width: auto;
    min-width: unset;
    margin: 0 10px;
    justify-content: center;
    align-items: center;
    height: 100%;
    flex: 0 0 32px;
    display: flex;
  }
`;

const BarContainer = styled.div`
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 9px;
  font-weight: bold;
  background: linear-gradient(to right, #99B7E8, #1A5ECC);
  border-radius: 10px;
  width: 98%;
  margin: 5px auto;
`;

const TitleValueContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  @media (max-width: 700px) {
    flex-direction: column;
    align-items: flex-end;
    justify-content: flex-end;
    width: auto;
    margin: 1px 0;
  }
`;

const TitleValueContainer2 = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  @media (max-width: 700px) {
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-start;
    width: auto;
    margin: 1px 0;
  }
`;

const TitlePrice = styled.div<{showData?: boolean}>`
  font-size: 10px;
  text-align: center;
  width: 80px;
  color: ${(props) => (props.showData ? '#3D5176' : 'transparent')};
  @media (max-width: 700px) {
    text-align: left;
  }
`;

const TitleTopPrice = styled.div<{showData?: boolean}>`
  font-size: 10px;
  text-align: center;
  width: 80px;
  color: ${(props) => (props.showData ? '#3D5176' : 'transparent')};
  font-family: 'Roboto bold';
  @media (max-width: 700px) {
    text-align: right;
  }
`;

const PriceText = styled.div<{showData?: boolean}>`
  font-size: 10px;
  text-align: center;
  color: ${(props) => (props.showData ? '#3D5176' : '')};
  font-family: 'Roboto bold';
`;

const AutoIconTextContainer = styled.div<{showData?: boolean}>`
  width: 2px;
  margin-left: 30px;
  margin-right: 30px;
  color: ${(props) => (props.showData ? '#3D5176' : 'transparent')};
  display: flex;
  flex-direction: column;
  align-items: center;
  @media (max-width: 700px) {
    flex-direction: row;
    align-items: flex-end;
    justify-content: flex-end;
    width: auto;
    margin: 1px 0;
  }
`;

const AutoIconTextContainer2 = styled.div<{showData?: boolean}>`
  width: 2px;
  margin-left: 30px;
  margin-right: 30px;
  color: ${(props) => (props.showData ? '#3D5176' : 'transparent')};
  display: flex;
  flex-direction: column;
  align-items: center;
  @media (max-width: 700px) {
    flex-direction: row;
    align-items: flex-start;
    justify-content: flex-start;
    width: auto;
    margin: 1px 0;
  }
`;

const IndicatorTriangleTop = styled.div`
  width: 0;
  height: 0;
  margin: auto;
  border-right: 6px solid transparent;
  border-left: 6px solid transparent;
  border-top: 6px solid #14346f;
  border-bottom: 0px solid transparent;
  @media (max-width: 700px) {
    border-top: none;
    border-left: 6px solid #14346f;
    border-bottom: 6px solid transparent;
    border-right: none;
    border-top: 6px solid transparent;
    margin-left: 10px;
  }
`;

const IndicatorTriangleBottom = styled.div`
  width: 0;  
  height: 0;
  margin: auto;
  border-right: 6px solid transparent;
  border-left: 6px solid transparent;
  border-top: 0px solid transparent;
  border-bottom: 8px solid #14346f;
  @media (max-width: 700px) {
    border-bottom: none;
    border-left: 8px solid transparent;
    border-top: 6px solid transparent;
    border-right: 6px solid #14346f;
    border-bottom: 6px solid transparent;
    margin-right: 10px;
  }
`;

export const BlankSpace = styled.div<{width?:string, height?:string}>`
  width: ${(props) => props.width || '100%'};
  height: ${(props) => props.height || '100%'};
`;
